import endpoints from "../../../../utils/endpoints";
import type {
  ApiRequestSpec,
  ApiResult,
  ChildOrderSnapshot,
  EnquirySnapshot,
  EstimateSnapshot,
  FlowContext,
  OrderSnapshot,
  PartConfirmationSnapshot,
  PartConversionState,
  ShipmentSnapshot,
  SnapshotOrderItem,
  SnapshotScope,
  FlowStep,
} from "../types";

type ApiFn = <T = any>(spec: ApiRequestSpec) => Promise<ApiResult<T>>;

const idOf = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value._id ? String(value._id) : undefined;
  return String(value);
};

const toIso = (value: any): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString();
};

export const normalizeOrderItems = (items: any[]): SnapshotOrderItem[] =>
  (items || []).map((item) => {
    const part = item?.partId && typeof item.partId === "object" ? item.partId : undefined;
    return {
      partId: idOf(item?.partId) || "",
      partNumber: item?.partNumber || part?.partNumber,
      rootPartNumber: item?.rootPartNumber || part?.rootPartNumber,
      partName: item?.partName || part?.partName || part?.name,
      orderedQuantity: Number(item?.orderedQuantity ?? item?.quantity ?? 0),
      mrp: item?.partMrp ?? part?.mrp,
    };
  });

/** Enquiry statuses that come after part conversion is completed. */
const PART_CONVERSION_DONE_STATUSES = [
  "PART_CONVERSION_COMPLETED",
  "DRAFT_ESTIMATE_SHARED",
  "FINAL_ESTIMATE_REQUESTED",
];

export const partConversionStateOf = (order: any): PartConversionState => {
  const start = order?.metaInfo?.partConversionStartTime;
  const end = order?.metaInfo?.partConversionEndTime;
  if (end || PART_CONVERSION_DONE_STATUSES.includes(order?.orderStatus)) return "COMPLETED";
  if (start || order?.orderStatus === "PART_CONVERSION_STARTED") return "IN_PROGRESS";
  return "NOT_STARTED";
};

export const normalizeEnquiry = (order: any): EnquirySnapshot => ({
  orderId: String(order._id),
  orderNo: order.orderNo,
  orderStatus: order.orderStatus,
  isTestOrder: Boolean(order.isTestOrder),
  requireEstimate: Boolean(order.requireEstimate),
  requirePriceEstimate: order.requirePriceEstimate === true,
  partConversion: partConversionStateOf(order),
  partConversionStartTime: toIso(order?.metaInfo?.partConversionStartTime),
  partConversionEndTime: toIso(order?.metaInfo?.partConversionEndTime),
  estimateSharedAt: toIso(order?.metaInfo?.estimateSharedAt),
  orderItems: normalizeOrderItems(order.orderItems),
  orderHandler: idOf(order.orderHandler),
  cancelledAt: toIso(order?.metaInfo?.cancelledAt),
});

export const activeAllocation = (order: any, supplierId?: string) => {
  const list: any[] = order?.allocatedTo || [];
  const active = list.filter((a) => a?.isActive !== false);
  if (supplierId) {
    const match = active.find((a) => idOf(a.supplierId) === supplierId);
    if (match) return match;
  }
  return active[active.length - 1] || list[list.length - 1];
};

export const normalizeChild = (order: any, supplierId?: string): ChildOrderSnapshot => {
  const allocation = activeAllocation(order, supplierId);
  return {
    orderId: String(order._id),
    orderNo: order.orderNo,
    orderStatus: order.orderStatus,
    supplierId: idOf(allocation?.supplierId),
    supplierStatus: allocation?.orderStatus,
    supplierInvoiceNo: allocation?.invoiceInfo?.invoiceNo,
    supplierInvoiceMediaId: idOf(allocation?.invoiceInfo?.invoiceMediaId),
    garaazInvoiceNo: order?.invoiceInfo?.invoiceNo,
    hasGaraazInvoice: Boolean(
      order?.invoiceInfo?.invoiceNo || order?.invoiceInfo?.invoiceMediaId
    ),
    transactionType: order.transactionType,
    shipmentId: order.shipmentId ? String(order.shipmentId) : undefined,
    dispatchRider: order?.metaInfo?.dispatchRider?.name,
    readyToDispatchTime: toIso(order?.metaInfo?.readyToDispatchTime),
    orderItems: normalizeOrderItems(order.orderItems),
    hasOrderETA: Boolean(order?.orderETA?.length),
    allocationActionType: allocation?.actionType,
  };
};

const normalizeEstimate = (estimate: any, splitId?: string): EstimateSnapshot => {
  const splits: any[] = estimate?.splits || [];
  const split =
    splits.find((s) => String(s._id) === splitId) || splits[splits.length - 1];
  return {
    estimateNo: estimate?.estimateNo,
    status: estimate?.status,
    splitId: split ? String(split._id) : undefined,
    splitStatus: split?.status,
    transactionType: split?.transactionType,
    payableBy: split?.payableBy,
    payableTo: split?.payableTo,
    deliveryOptionId: split?.selectedDeliveryOptionId
      ? String(split.selectedDeliveryOptionId)
      : split?.deliveryOptions?.[0]?._id
      ? String(split.deliveryOptions[0]._id)
      : undefined,
    expiresAt: toIso(estimate?.expiresAt),
  };
};

const normalizePartConfirmation = (
  requests: any,
  confirmed: any,
  supplierId: string
): PartConfirmationSnapshot => {
  const list: any[] = requests?.requests || [];
  const forSupplier =
    list.find((r) => String(r.supplierId) === supplierId) || list[list.length - 1];
  const parts: any[] = confirmed?.parts || [];
  const answered = parts.filter((p) => p.status === "confirmed" || p.status === "declined");
  const total = forSupplier?.parts?.length || parts.length;
  return {
    requestId: forSupplier?.requestId,
    supplierId: forSupplier?.supplierId ? String(forSupplier.supplierId) : undefined,
    status: forSupplier?.status,
    responded: Boolean(forSupplier) && answered.length > 0 && answered.length >= (total || 1),
    confirmedParts: answered.filter((p) => p.status === "confirmed").length,
    totalParts: total,
  };
};

const normalizeShipment = (payload: any): ShipmentSnapshot | undefined => {
  const shipment = payload?.data ?? payload;
  if (!shipment) return undefined;
  return {
    shipmentId: String(shipment.shipmentId || shipment._id || shipment.id),
    status: shipment.status,
    riderName: shipment?.deliveryTracking?.deliveryAgent?.name,
    dispatchMode: shipment.dispatchMode,
  };
};

export const fetchOrderRaw = (api: ApiFn, orderId: string, isPoll = true) =>
  api<any>({
    label: "GET order",
    method: "GET",
    path: `/${endpoints.getGaraazOrder(orderId)}`,
    isPoll,
  }).then((r) => r.data);

export const UI_SNAPSHOT_SCOPE: SnapshotScope = {
  enquiry: true,
  child: true,
  estimate: true,
  partConfirmation: true,
  shipment: true,
};

/** Default reads for a step: the order that just changed, not every related resource. */
export const inferSnapshotScope = (step: FlowStep, ctx: FlowContext): SnapshotScope => {
  const id = step.id;
  if (id.startsWith("part_confirmation")) return { enquiry: true, partConfirmation: true };
  if (id.startsWith("estimate")) {
    return { enquiry: true, estimate: Boolean(ctx.estimate), child: Boolean(ctx.child) };
  }
  if (id === "invoice_event" || id === "ready_to_dispatch" || id === "assign_rider") {
    return { child: true };
  }
  if (id === "create_shipment") return { child: true };
  if (id === "mark_delivered" || id === "delivered_event") return { child: true, shipment: true };
  if (id.includes("enquiry") || id.includes("part_conversion")) return { enquiry: true };
  if (ctx.child) return { child: true };
  if (ctx.enquiry) return { enquiry: true };
  return {};
};

export const snapshotMissingScope = (ctx: FlowContext, scope: SnapshotScope): boolean => {
  const snap = ctx.snapshot;
  if (!snap) return Boolean(ctx.enquiry || ctx.child);
  if (scope.enquiry && ctx.enquiry && !snap.enquiry) return true;
  if (scope.child && ctx.child && !snap.child) return true;
  if (scope.estimate && ctx.estimate && !snap.estimate) return true;
  if (scope.partConfirmation && snap.partConfirmation === undefined) return true;
  if (scope.shipment && (ctx.shipment?.shipmentId || snap.child?.shipmentId) && !snap.shipment) {
    return true;
  }
  return false;
};

/**
 * Fetches only the requested resources and merges them onto the last snapshot.
 * A full kitchen-sink refresh on every step was ~5 GETs × 2 per step.
 */
export const fetchSnapshot = async (
  ctx: FlowContext,
  api: ApiFn,
  scope?: SnapshotScope
): Promise<OrderSnapshot> => {
  const resolved: SnapshotScope = scope ?? {
    enquiry: Boolean(ctx.enquiry),
    child: Boolean(ctx.child),
  };
  const snapshot: OrderSnapshot = {
    fetchedAt: Date.now(),
    enquiry: ctx.snapshot?.enquiry,
    child: ctx.snapshot?.child,
    estimate: ctx.snapshot?.estimate,
    partConfirmation: ctx.snapshot?.partConfirmation,
    shipment: ctx.snapshot?.shipment,
  };

  const tasks: Promise<void>[] = [];

  if (resolved.enquiry && ctx.enquiry) {
    tasks.push(
      fetchOrderRaw(api, ctx.enquiry.orderId).then((raw) => {
        snapshot.enquiry = normalizeEnquiry(raw);
      })
    );
  }

  if (resolved.child && ctx.child) {
    tasks.push(
      fetchOrderRaw(api, ctx.child.orderId).then((raw) => {
        snapshot.child = normalizeChild(raw, ctx.supplier.id);
      })
    );
  }

  if (resolved.estimate && ctx.estimate?.estimateNo) {
    tasks.push(
      api<any>({
        label: "GET estimate",
        method: "GET",
        path: `/v1/estimate/${ctx.estimate.estimateNo}`,
        isPoll: true,
      })
        .then((est) => {
          snapshot.estimate = normalizeEstimate(est.data, ctx.estimate?.splitId);
        })
        .catch(() => {
          snapshot.estimate = { estimateNo: ctx.estimate!.estimateNo };
        })
    );
  }

  if (resolved.partConfirmation && ctx.enquiry) {
    tasks.push(
      Promise.all([
        api<any>({
          label: "GET part-confirmation requests",
          method: "GET",
          path: endpoints.getPartConfirmationRequests(ctx.enquiry.orderNo),
          isPoll: true,
        }),
        api<any>({
          label: "GET part-confirmation responses",
          method: "GET",
          path: endpoints.getPartConfirmationResponses(ctx.enquiry.orderNo, ctx.supplier.id),
          isPoll: true,
        }),
      ])
        .then(([requests, confirmed]) => {
          snapshot.partConfirmation = normalizePartConfirmation(
            requests.data,
            confirmed.data,
            ctx.supplier.id
          );
        })
        .catch(() => {
          snapshot.partConfirmation = {
            requestId: ctx.partConfirmation?.requestId,
            supplierId: ctx.partConfirmation?.supplierId,
            responded: false,
            confirmedParts: 0,
            totalParts: 0,
          };
        })
    );
  }

  const shipmentId = ctx.shipment?.shipmentId || snapshot.child?.shipmentId;
  if (resolved.shipment && shipmentId) {
    tasks.push(
      api<any>({
        label: "GET shipment",
        method: "GET",
        path: endpoints.updateShipment(shipmentId),
        isPoll: true,
      })
        .then((shipment) => {
          snapshot.shipment = normalizeShipment(shipment.data);
        })
        .catch(() => {
          snapshot.shipment = { shipmentId };
        })
    );
  }

  await Promise.all(tasks);
  return snapshot;
};

export const describeSnapshot = (s?: OrderSnapshot): string => {
  if (!s) return "no order yet";
  const parts: string[] = [];
  if (s.enquiry) {
    parts.push(`enquiry ${s.enquiry.orderNo}=${s.enquiry.orderStatus}`);
    if (s.enquiry.partConversion !== "NOT_STARTED") {
      parts.push(`partConversion=${s.enquiry.partConversion}`);
    }
  }
  if (s.partConfirmation) {
    parts.push(
      `partConfirmation=${s.partConfirmation.responded ? "responded" : "pending"}`
    );
  }
  if (s.estimate) {
    parts.push(`estimate ${s.estimate.estimateNo}=${s.estimate.status || "?"}`);
  }
  if (s.child) {
    parts.push(
      `order ${s.child.orderNo}=${s.child.orderStatus}, supplier=${
        s.child.supplierStatus || "-"
      }, invoice=${s.child.hasGaraazInvoice ? "yes" : "no"}`
    );
  }
  if (s.shipment) parts.push(`shipment=${s.shipment.status || "?"}`);
  return parts.join(" | ");
};
